import { useState, useEffect, useCallback } from "react";
import { signatureSpendingApi } from "@services/api/organizations";

function fmtEur(v: number, decimals = 2): string {
  return v.toLocaleString("fr-FR", { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + "\u00A0\u20AC";
}

export function SignatureSpending({ hideSoldeRecharge = false }: { hideSoldeRecharge?: boolean } = {}) {
  const [spending, setSpending] = useState<{
    solde: number;
    total_spent: number;
    transaction_count: number;
    signature_level: string;
  } | null>(null);
  const [transactions, setTransactions] = useState<
    { id: string; doc_id: string; signer_name: string; signature_type: string; level: string; unit_cost: number; created_at: string }[]
  >([]);
  const [txPage, setTxPage] = useState(0);
  const TX_PER_PAGE = 20;

  const formatDate = (iso: string) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const fetchSpending = useCallback(async () => {
    try {
      const [s, tx] = await Promise.all([
        signatureSpendingApi.getSpending(),
        signatureSpendingApi.getTransactions(TX_PER_PAGE, txPage * TX_PER_PAGE),
      ]);
      setSpending(s);
      setTransactions(tx);
    } catch {
      // API not available yet
    }
  }, [txPage]);

  useEffect(() => {
    fetchSpending();
  }, [fetchSpending]);

  return (
    <div className="flex flex-col gap-6">
      <h2
        className="text-h1 font-semibold"
        style={{ fontFamily: "Poppins", color: "#111827" }}
      >
        Signatures - Consommation
      </h2>

      {!hideSoldeRecharge && (
        <div className="flex flex-row gap-4">
          <div className="flex-1 p-5 rounded-xl" style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}>
            <span className="text-h4 block mb-1" style={{ fontFamily: "Poppins", color: "#6B7280" }}>
              Solde communications
            </span>
            <span className="text-hx font-bold" style={{ fontFamily: "Poppins", color: "#111827" }}>
              {spending ? fmtEur(spending.solde) : "--"}
            </span>
          </div>
          <div className="flex-1 p-5 rounded-xl" style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}>
            <span className="text-h4 block mb-1" style={{ fontFamily: "Poppins", color: "#6B7280" }}>
              Total depense (signatures)
            </span>
            <span className="text-hx font-bold" style={{ fontFamily: "Poppins", color: "#111827" }}>
              {spending ? fmtEur(spending.total_spent) : "--"}
            </span>
          </div>
          <div className="flex-1 p-5 rounded-xl" style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}>
            <span className="text-h4 block mb-1" style={{ fontFamily: "Poppins", color: "#6B7280" }}>
              Signatures
            </span>
            <span className="text-hx font-bold" style={{ fontFamily: "Poppins", color: "#111827" }}>
              {spending?.transaction_count ?? "--"}
            </span>
          </div>
        </div>
      )}

      {hideSoldeRecharge && (
        <div className="flex flex-row gap-4">
          <div className="flex-1 p-5 rounded-xl" style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}>
            <span className="text-h4 block mb-1" style={{ fontFamily: "Poppins", color: "#6B7280" }}>
              Total depense (signatures)
            </span>
            <span className="text-hx font-bold" style={{ fontFamily: "Poppins", color: "#111827" }}>
              {spending ? fmtEur(spending.total_spent) : "--"}
            </span>
          </div>
          <div className="flex-1 p-5 rounded-xl" style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}>
            <span className="text-h4 block mb-1" style={{ fontFamily: "Poppins", color: "#6B7280" }}>
              Signatures
            </span>
            <span className="text-hx font-bold" style={{ fontFamily: "Poppins", color: "#111827" }}>
              {spending?.transaction_count ?? "--"}
            </span>
          </div>
        </div>
      )}

      {/* Pricing */}
      <div className="p-4 rounded-xl" style={{ background: "#F9FAFB", border: "1px solid #E5E7EB" }}>
        <span className="text-h2 font-semibold block mb-2" style={{ fontFamily: "Poppins", color: "#111827" }}>
          Tarification
        </span>
        <div className="flex flex-col gap-1">
          <span className="text-h3" style={{ fontFamily: "Poppins", color: "#6B7280" }}>
            Signature simple : <strong style={{ color: "#059669" }}>0,05 {"\u20AC"}</strong> par signature
          </span>
          <span className="text-h3" style={{ fontFamily: "Poppins", color: "#6B7280" }}>
            Signature avancee : <strong style={{ color: "#2563EB" }}>0,20 {"\u20AC"}</strong> par signature (verification SMS + PAdES + horodatage)
          </span>
        </div>
      </div>

      {/* Transaction history */}
      {transactions.length > 0 && (
        <div className="flex flex-col gap-3">
          <span
            className="text-h2 font-semibold"
            style={{ fontFamily: "Poppins", color: "#374151" }}
          >
            Historique des transactions
          </span>
          <div className="flex flex-col gap-1">
            {transactions.map((tx) => (
              <div
                key={tx.id}
                className="flex flex-row items-center justify-between p-3 rounded-lg"
                style={{ background: "#FAFAFA", border: "1px solid #F3F4F6" }}
              >
                <div className="flex flex-col gap-0.5">
                  <span className="text-h3" style={{ fontFamily: "Poppins", color: "#111827" }}>
                    {tx.signer_name || "--"}
                  </span>
                  <span className="text-h5" style={{ fontFamily: "Poppins", color: "#9CA3AF" }}>
                    {formatDate(tx.created_at)}
                  </span>
                </div>
                <div className="flex flex-row items-center gap-3">
                  <span
                    className="text-h5 px-2 py-0.5 rounded-full"
                    style={{
                      fontFamily: "Poppins",
                      color: tx.level === "advanced" ? "#1E40AF" : "#065F46",
                      background: tx.level === "advanced" ? "#DBEAFE" : "#D1FAE5",
                    }}
                  >
                    {tx.level === "advanced" ? "Avancee" : "Simple"}
                  </span>
                  <span className="text-h3 font-medium" style={{ fontFamily: "Poppins", color: "#DC2626" }}>
                    -{fmtEur(tx.unit_cost, 2)}
                  </span>
                </div>
              </div>
            ))}
          </div>
          {/* Pagination */}
          <div className="flex flex-row items-center gap-3 mt-1">
            <button
              onClick={() => setTxPage((p) => Math.max(0, p - 1))}
              disabled={txPage === 0}
              className="text-h3 font-medium px-3 py-1 rounded"
              style={{
                fontFamily: "Poppins",
                color: txPage === 0 ? "#D1D5DB" : "#374151",
                border: "1px solid #E5E7EB",
                cursor: txPage === 0 ? "default" : "pointer",
              }}
            >
              Precedent
            </button>
            <span
              className="text-h4"
              style={{ fontFamily: "Poppins", color: "#6B7280" }}
            >
              Page {txPage + 1}
            </span>
            <button
              onClick={() => setTxPage((p) => p + 1)}
              disabled={transactions.length < TX_PER_PAGE}
              className="text-h3 font-medium px-3 py-1 rounded"
              style={{
                fontFamily: "Poppins",
                color: transactions.length < TX_PER_PAGE ? "#D1D5DB" : "#374151",
                border: "1px solid #E5E7EB",
                cursor: transactions.length < TX_PER_PAGE ? "default" : "pointer",
              }}
            >
              Suivant
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
